import { useState, useEffect, useRef, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/api";
import { AuthContext } from "../context/AuthContext";
import ReportarProblema from "../components/ReportarProblema";

const font = "Montserrat, sans-serif";

const SERVICE_LABELS = {
  aire_acondicionado: "Aire Acondicionado", albanileria: "Albañilería",
  arquitectura: "Arquitectura", belleza_personal: "Belleza Personal",
  carpinteria: "Carpintería", cerrajeria: "Cerrajería",
  control_plagas: "Control de Plagas", cuidado_mayores: "Cuidado de Mayores",
  cuidado_ninos: "Cuidado de Niños", decoradores: "Decoradores",
  electricista: "Electricista", entrenamiento: "Entrenamiento",
  gasista: "Gasista", herrero: "Herrero", infantiles: "Infantiles",
  instalador_general: "Instalador General", jardinero: "Jardinero",
  mascotas: "Mascotas", mecanica_automotriz: "Mecánica Automotriz",
  mudanzas: "Mudanzas", piletas: "Piletas", pintores: "Pintores",
  plomeros: "Plomeros", seguridad: "Seguridad",
  servicio_domestico: "Servicio Doméstico", tapicero: "Tapicero",
  tecnologia: "Tecnología",
};

function formatTime(dateStr) {
  const d = new Date(dateStr);
  const today = new Date();
  const isToday =
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear();
  if (isToday) return d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" }) +
    " " + d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
}

function groupMessagesByDate(messages) {
  const groups = [];
  let lastDate = null;
  messages.forEach(msg => {
    const d = new Date(msg.created_at);
    const dateKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (dateKey !== lastDate) {
      lastDate = dateKey;
      const today = new Date();
      const isToday = d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
      const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
      const isYesterday = d.getDate() === yesterday.getDate() && d.getMonth() === yesterday.getMonth() && d.getFullYear() === yesterday.getFullYear();
      groups.push({ type: "date", label: isToday ? "Hoy" : isYesterday ? "Ayer" : d.toLocaleDateString("es-AR", { day: "2-digit", month: "long", year: "numeric" }) });
    }
    groups.push({ type: "message", ...msg });
  });
  return groups;
}

export default function ChatPage() {
  const { requestId } = useParams();
  const navigate      = useNavigate();
  const { user }      = useContext(AuthContext);

  const [chatData,    setChatData]    = useState(null);
  const [messages,    setMessages]    = useState([]);
  const [inputText,   setInputText]   = useState("");
  const [loadingData, setLoadingData] = useState(true);
  const [loadError,   setLoadError]   = useState("");
  const [sending,     setSending]     = useState(false);
  // Antes mandar y refrescar fallaban callados: el mensaje volvía al input
  // sin aviso y el chat se congelaba sin que nadie lo supiera (26-10-2026).
  const [sendError,   setSendError]   = useState("");
  const [sinConexion, setSinConexion] = useState(false);
  const fallasPolling = useRef(0);

  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);
  const pollingRef     = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    if (!requestId) return;
    setLoadingData(true);
    api.get(`/chat/${requestId}`)
      .then(res => {
        setChatData(res.data.request);
        setMessages(res.data.messages || []);
        api.patch(`/chat/${requestId}/read`).catch(() => {});
      })
      .catch(err => {
        const status = err.response?.status;
        if (status === 403) setLoadError("No tenés acceso a este chat.");
        else if (status === 400) setLoadError("El chat no está disponible para esta solicitud.");
        else setLoadError("No se pudo cargar el chat.");
      })
      .finally(() => setLoadingData(false));
  }, [requestId]);

  useEffect(() => { scrollToBottom(); }, [messages]);

  useEffect(() => {
    if (!chatData) return;
    pollingRef.current = setInterval(() => {
      api.get(`/chat/${requestId}`)
        .then(res => {
          fallasPolling.current = 0;
          setSinConexion(false);
          const newMessages = res.data.messages || [];
          setMessages(prev => {
            if (newMessages.length !== prev.length) {
              api.patch(`/chat/${requestId}/read`).catch(() => {});
              return newMessages;
            }
            return prev;
          });
        })
        .catch(() => {
          // Una falla suelta no se muestra; dos seguidas (10 s) sí.
          fallasPolling.current += 1;
          if (fallasPolling.current >= 2) setSinConexion(true);
        });
    }, 5000);
    return () => clearInterval(pollingRef.current);
  }, [chatData, requestId]);

  const handleSend = async () => {
    const body = inputText.trim();
    if (!body || sending) return;
    setSending(true);
    setSendError("");
    setInputText("");
    try {
      const res = await api.post(`/chat/${requestId}`, { body });
      setMessages(prev => [...prev, res.data.message]);
    } catch (err) {
      setInputText(body);
      setSendError(err.response?.data?.error || "No se pudo enviar el mensaje. Probá de nuevo.");
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loadingData) return (
    <div style={{ minHeight: "100vh", background: "#f5f7fa", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: font }}>
      <div style={{ textAlign: "center", color: "#7a93a4" }}>
        <span style={{ display: "inline-block", width: 28, height: 28, border: "3px solid #dce5ec", borderTopColor: "#163244", borderRadius: "50%", animation: "spin 0.7s linear infinite", marginBottom: 12 }} />
        <div style={{ fontWeight: 600 }}>Cargando chat...</div>
      </div>
      <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
    </div>
  );

  // ── Error ──────────────────────────────────────────────────────────────────
  if (loadError) return (
    <div style={{ minHeight: "100vh", background: "#f5f7fa", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: font }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontWeight: 600, color: "#c0392b", marginBottom: 16 }}>{loadError}</div>
        <button onClick={() => navigate(-1)} style={{ padding: "10px 24px", borderRadius: 50, background: "#163244", color: "#fff", border: "none", fontFamily: font, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
          ← Volver
        </button>
      </div>
    </div>
  );

  const isClient     = user?.id === chatData?.client_id;
  const otherName    = isClient ? chatData?.provider_name : chatData?.client_name;
  const serviceLabel = SERVICE_LABELS[chatData?.service_key] || chatData?.service_key;
  const grouped      = groupMessagesByDate(messages);
  const isCompleted  = chatData?.status === "completed";

  return (
    <>
      <div style={{
        fontFamily: font, background: "#f5f7fa",
        minHeight: "100vh", padding: "24px 24px 0",
        display: "flex", flexDirection: "column",
      }}>
        <div style={{
          maxWidth: 780, width: "100%", margin: "0 auto",
          display: "flex", flexDirection: "column",
          height: "calc(100vh - 88px)",
        }}>

          {/* Header */}
          <div style={{
            background: "#163244", borderRadius: 16, padding: "16px 20px",
            marginBottom: 12, display: "flex", alignItems: "center", gap: 14,
            boxShadow: "0 4px 20px rgba(22,50,68,0.15)", position: "relative", overflow: "hidden", flexShrink: 0,
          }}>
            {/* Textura */}
            <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 90% 20%, rgba(231,164,50,0.12) 0%, transparent 50%)", pointerEvents: "none" }} />

            <button onClick={() => navigate(-1)} style={{ background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 9, width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", flexShrink: 0, zIndex: 1 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
            </button>

            {/* Avatar */}
            <div style={{ width: 44, height: 44, borderRadius: "50%", flexShrink: 0, background: "#E7A432", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 18, color: "#163244", zIndex: 1 }}>
              {otherName?.[0] || "?"}
            </div>

            <div style={{ flex: 1, minWidth: 0, zIndex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#fff", marginBottom: 2 }}>{otherName}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                {serviceLabel} · Solicitud #{requestId}
              </div>
            </div>

            <span style={{
              padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, zIndex: 1,
              color: isCompleted ? "#b8820d" : "#1b7f4e",
              background: isCompleted ? "rgba(231,164,50,0.15)" : "rgba(27,127,78,0.15)",
              whiteSpace: "nowrap", flexShrink: 0,
            }}>
              {isCompleted ? "Completado" : "En curso"}
            </span>
          </div>

          {/* A la vista en el chat, para cliente y prestador (Guías, punto 8). */}
          <ReportarProblema solicitudId={requestId} variante="chat" />

          {/* Área mensajes */}
          <div style={{
            flex: 1, overflowY: "auto",
            background: "#fff", borderRadius: 16,
            padding: "20px 18px", marginBottom: 12,
            display: "flex", flexDirection: "column", gap: 2,
            border: "1px solid rgba(0,0,0,0.07)",
            boxShadow: "0 2px 8px rgba(22,50,68,0.04)",
          }}>

            {messages.length === 0 && (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#aab8c2", textAlign: "center", padding: "40px 20px" }}>
                <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#f0f5f8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 14 }}>💬</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#163244", marginBottom: 6 }}>Iniciá la conversación</div>
                <div style={{ fontSize: 13, color: "#7a93a4" }}>Coordiná los detalles del trabajo con {otherName}.</div>
              </div>
            )}

            {grouped.map((item, i) => {
              if (item.type === "date") return (
                <div key={`date-${i}`} style={{ textAlign: "center", margin: "14px 0 6px", fontSize: 11, color: "#aab8c2", fontWeight: 600 }}>
                  <span style={{ background: "#f5f7fa", borderRadius: 20, padding: "3px 14px", border: "1px solid rgba(0,0,0,0.06)" }}>
                    {item.label}
                  </span>
                </div>
              );

              const isMine = item.sender_id === user?.id;

              return (
                <div key={item.id} style={{ display: "flex", justifyContent: isMine ? "flex-end" : "flex-start", marginBottom: 3 }}>
                  {/* Avatar del otro */}
                  {!isMine && (
                    <div style={{ width: 30, height: 30, borderRadius: "50%", background: "#163244", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: "#E7A432", flexShrink: 0, marginRight: 8, alignSelf: "flex-end", marginBottom: 2 }}>
                      {otherName?.[0] || "?"}
                    </div>
                  )}
                  <div style={{
                    maxWidth: "68%",
                    background: isMine ? "#163244" : "#f5f7fa",
                    color: isMine ? "#fff" : "#163244",
                    borderRadius: isMine ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                    padding: "10px 14px",
                    boxShadow: isMine ? "0 2px 8px rgba(22,50,68,0.15)" : "0 1px 4px rgba(22,50,68,0.05)",
                  }}>
                    <div style={{ fontSize: 14, lineHeight: 1.55, wordBreak: "break-word" }}>{item.body}</div>
                    <div style={{ fontSize: 10, marginTop: 5, textAlign: "right", color: isMine ? "rgba(255,255,255,0.45)" : "#aab8c2", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                      {formatTime(item.created_at)}
                      {isMine && <span style={{ fontSize: 11, color: item.read ? "#E7A432" : "rgba(255,255,255,0.45)" }}>{item.read ? "✓✓" : "✓"}</span>}
                    </div>
                  </div>
                </div>
              );
            })}

            <div ref={messagesEndRef} />
          </div>

          {sinConexion && (
            <div style={{ marginBottom: 8, padding: "8px 14px", borderRadius: 10, background: "rgba(192,57,43,0.06)", border: "1px solid rgba(192,57,43,0.25)", color: "#c0392b", fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
              Se cortó la conexión con el chat: los mensajes nuevos pueden no estar llegando. Seguimos intentando.
            </div>
          )}
          {sendError && (
            <div style={{ marginBottom: 8, padding: "8px 14px", borderRadius: 10, background: "rgba(192,57,43,0.06)", border: "1px solid rgba(192,57,43,0.25)", color: "#c0392b", fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
              {sendError}
            </div>
          )}

          {/* Input */}
          {isCompleted ? (
            <div style={{ background: "#fff", borderRadius: 14, padding: "14px 20px", textAlign: "center", color: "#7a93a4", fontSize: 13, border: "1px solid rgba(0,0,0,0.07)", marginBottom: 24, flexShrink: 0 }}>
              Este trabajo fue completado. El chat está en modo solo lectura.
            </div>
          ) : (
            <div style={{ background: "#fff", borderRadius: 16, padding: "10px 10px 10px 16px", border: "1px solid rgba(0,0,0,0.07)", boxShadow: "0 2px 8px rgba(22,50,68,0.06)", display: "flex", alignItems: "flex-end", gap: 10, marginBottom: 24, flexShrink: 0 }}>
              <textarea
                ref={inputRef}
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`Escribí un mensaje a ${otherName}...`}
                rows={1}
                style={{ flex: 1, border: "none", outline: "none", resize: "none", fontFamily: font, fontSize: 14, color: "#163244", background: "transparent", lineHeight: 1.55, maxHeight: 120, overflowY: "auto", padding: "7px 0" }}
                onInput={e => { e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px"; }}
              />
              <button
                onClick={handleSend}
                disabled={!inputText.trim() || sending}
                style={{
                  width: 42, height: 42, borderRadius: "50%", border: "none", flexShrink: 0,
                  background: !inputText.trim() || sending ? "#f0f5f8" : "#163244",
                  color: !inputText.trim() || sending ? "#aab8c2" : "#E7A432",
                  cursor: !inputText.trim() || sending ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 18, transition: "all 0.2s",
                }}
              >
                {sending
                  ? <span style={{ width: 14, height: 14, border: "2px solid #aab8c2", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} />
                  : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
                }
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}
